import React from 'react';
import moment from 'moment-timezone';
import { joinClassNames } from '@utahdts/dts-react-common';
import { MOUNTAIN_TZ, toCalendarDate } from './isoDate';

// Canonical "last updated" timestamp field present on the bitemporal
// records (WACs, approvals, patents). Tables default-sort by this field
// descending (newest first). Records whose GraphQL query does not expose a
// timestamp (companies, drugs/listings, users) simply keep their incoming
// order under this comparator.
export const LAST_UPDATED_FIELD = 'createdDate';

// Numeric epoch (ms) for a date-ish value in Mountain Time, or NaN when the
// value is missing/unparseable. Used by the date-aware comparators so date
// columns sort chronologically rather than lexically.
const toEpoch = (value) => {
  if (value === null || value === undefined || value === '') {
    return NaN;
  }
  const m = moment(value);
  return m.isValid() ? m.valueOf() : NaN;
};

// Descending date comparator (newest first). Missing/invalid dates sort to
// the bottom so freshly-created records with a timestamp lead the table.
export const compareDatesDesc =
  (field = LAST_UPDATED_FIELD) =>
  (a, b) => {
    const av = toEpoch(a && a[field]);
    const bv = toEpoch(b && b[field]);
    const an = Number.isNaN(av) ? -Infinity : av;
    const bn = Number.isNaN(bv) ? -Infinity : bv;
    return bn - an;
  };

// Returns a new array sorted by the given timestamp field descending. Safe to
// call with non-arrays (returned unchanged) and with records missing the field
// (stable-ish: they all compare equal and keep relative order on a stable sort).
export const sortByLastUpdatedDesc = (list, field = LAST_UPDATED_FIELD) =>
  Array.isArray(list) ? [...list].sort(compareDatesDesc(field)) : list;

// Defense-in-depth for the "never display a hidden document" rule: filter out
// soft-deleted (hidden === true) pharmacy-* records before they reach a list.
// The API already excludes hidden rows server-side; this guarantees the UI can
// never render one even if a hidden document slips through. Safe on non-arrays
// (returned unchanged).
export const visibleRecords = (list) =>
  Array.isArray(list) ? list.filter((record) => record && record.hidden !== true) : list;

export const formatDate = (timestamp) =>
  moment(timestamp).tz(MOUNTAIN_TZ).format('MM/DD/YYYY');

export const createArray = (start, stop, step) =>
  Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

export const COLOR_PALETTE = [
  '#44AA99',
  '#117733',
  '#999933',
  '#DDCC77',
  '#88CCEE',
  '#AA4499',
  '#CC6677',
  '#882255',
  '#332288',
];

export const checkURL = (url) => {
  const expression =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g;
  const regex = new RegExp(expression);
  if (!url.match(regex)) {
    url = 'https://' + url;
  }
  return url;
};

export const statuses = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
};

export const truncate = (str, n) => {
  return str.length > n ? str.substr(0, n - 1) + '...' : str;
};

export const textTd = (columnInfo, data) => {
  return (
    <td
      key={joinClassNames(columnInfo.field, data.name)}
      className="word-break"
    >
      {data[columnInfo.field] && truncate(data[columnInfo.field], 250)}
    </td>
  );
};

// Format a true instant (e.g. "Date Submitted" / createdDate) in Mountain Time
// so its wall-clock date/time is America/Denver regardless of viewer locale.
// Falls back to regex reformatting for strings moment can't parse.
const formatInstant = (value) => {
  const m = value ? moment(value) : null;
  if (m?.isValid()) {
    return m.tz(MOUNTAIN_TZ).format('MM/DD/YYYY HH:mm:ss');
  }
  const isoMatch = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/.exec(
    value || '',
  );
  if (isoMatch) {
    return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]} ${isoMatch[4]}:${isoMatch[5]}:${isoMatch[6]}`;
  }
  const usMatch = /(\d{2})\/(\d{2})\/(\d{4})T(\d{2}):(\d{2}):(\d{2})/.exec(
    value || '',
  );
  if (usMatch) {
    return `${usMatch[1]}/${usMatch[2]}/${usMatch[3]} ${usMatch[4]}:${usMatch[5]}:${usMatch[6]}`;
  }
  return value || '';
};

export const dateTd = (columnInfo, data, full) => {
  const value = data[columnInfo.field];
  // full => a true instant (Date Submitted) rendered in Mountain Time.
  // Otherwise a NAIVE calendar date (WAC effective/increase date, FDA approval
  // date, patent expiration date) rendered literally — zoning a naive date
  // into Mountain Time shifts it a calendar day (the reported drift).
  const date = full ? formatInstant(value) : toCalendarDate(value) || value || '';
  return <td key={joinClassNames(columnInfo.field, data.name)}>{date}</td>;
};

// The parallel v2 stack uses the -v2 report Cloud Function (which reads the
// cloned default-v2 database). USE_V2_BACKEND is injected at build time by
// webpack DefinePlugin (empty => the live v1 backend). Gen 2 functions still
// resolve via the classic cloudfunctions.net/<name> alias, so only the function
// name changes.
const REPORT_FUNCTION =
  process.env.USE_V2_BACKEND === 'true' ? 'gen-report-ws-v2' : 'gen-report-ws';

export const CONTEXT_URL_REPORT = {
  LOCAL:
    `https://us-central1-ut-uid-pharma-dev.cloudfunctions.net/${REPORT_FUNCTION}`,
  DEV: `https://us-central1-ut-uid-pharma-dev.cloudfunctions.net/${REPORT_FUNCTION}`,
  AT: `https://us-central1-ut-uid-pharma-at.cloudfunctions.net/${REPORT_FUNCTION}`,
  PROD: `https://us-central1-ut-uid-pharma-prod.cloudfunctions.net/${REPORT_FUNCTION}`,
};
